/* TGC Shipping Protection widget.
   Auto-adds a protection product priced from a variant ladder, sized to the cart
   value in $50 tiers. Opt-out is remembered via the _shipping_protection cart
   attribute (absent or "on" = on by default; "off" = customer declined).

   Owner rules (2026-08-25/26, after order #21587 charged protection on a
   gift-card-only order): gift cards are never insured, and a cart with nothing
   that ships gets no protection at all. Non-shipping add-on lines (dye, glitter
   strap, priority, rush) DO count toward the insured value because they are part
   of the boot that ships and would have to be remade if it were lost. */
class ShippingProtection extends HTMLElement {
  connectedCallback() {
    this.productId = parseInt(this.dataset.productId, 10);
    this.percent = parseFloat(this.dataset.percent);
    this.minimumCents = parseInt(this.dataset.minimumCents, 10);
    this.variants = JSON.parse(this.dataset.variants || '[]')
      .sort((a, b) => a.priceCents - b.priceCents);
    this.toggleBtn = this.querySelector('[data-toggle]');
    this.priceSpan = this.querySelector('[data-price]');
    this.isProcessing = false;

    if (!this.productId || this.variants.length === 0) {
      this.hidden = true;
      return;
    }

    this.toggleBtn.addEventListener('click', () => this.handleToggle());

    this.boundEvaluate = () => this.evaluate();
    document.addEventListener('on:cart:change', this.boundEvaluate);

    // Removing the protection line item directly counts as opting out,
    // otherwise the widget would immediately re-add it.
    this.boundRemoveClick = (evt) => {
      if (evt.target.closest('[data-shipping-protection-remove]')) {
        this.setOptState('off');
      }
    };
    document.addEventListener('click', this.boundRemoveClick);

    this.evaluate();
  }

  disconnectedCallback() {
    document.removeEventListener('on:cart:change', this.boundEvaluate);
    document.removeEventListener('click', this.boundRemoveClick);
  }

  async handleToggle() {
    const turningOn = this.getAttribute('data-state') !== 'on';
    await this.setOptState(turningOn ? 'on' : 'off');
    this.evaluate();
  }

  setOptState(state) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { _shipping_protection: state } })
    }).catch((err) => console.error('ShippingProtection: attribute update failed', err));
  }

  async evaluate() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      const state = cart.attributes && cart.attributes._shipping_protection === 'off' ? 'off' : 'on';
      const protectionLine = cart.items.find((item) => item.product_id === this.productId);

      // Protection only applies when at least one line physically ships.
      // A gift-card-only cart (or a cart of add-ons with nothing to ship) gets none.
      const hasShippable = cart.items.some((item) => this.isShippable(item));

      // Insured value, in cents = every line except the protection item and gift
      // cards (add-on lines ride along with the boot they belong to). Zero when
      // nothing ships, which removes any protection line and hides the widget.
      const goodsCents = hasShippable
        ? cart.items.reduce((sum, item) => (this.isInsurable(item) ? sum + item.final_line_price : sum), 0)
        : 0;

      const desired = this.desiredVariant(goodsCents);
      let changed = false;

      this.hidden = goodsCents === 0;

      if (state === 'off' || goodsCents === 0) {
        if (protectionLine) {
          await this.changeLine(protectionLine.key, 0);
          changed = true;
        }
      } else if (!protectionLine) {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: desired.id, quantity: 1 }] })
        });
        changed = true;
      } else if (protectionLine.variant_id !== desired.id) {
        // Swap tiers: remove the old line, then add the correct variant.
        await this.changeLine(protectionLine.key, 0);
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: desired.id, quantity: 1 }] })
        });
        changed = true;
      } else if (protectionLine.quantity !== 1) {
        await this.changeLine(protectionLine.key, 1);
        changed = true;
      }

      this.updateUI(state, desired);

      if (changed) {
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
      }
    } catch (err) {
      console.error('ShippingProtection: evaluate failed', err);
    } finally {
      this.isProcessing = false;
    }
  }

  /* A real shipped product: not the protection item, not a gift card, and
     flagged by Shopify as requiring shipping. At least one of these must be in
     the cart for protection to apply at all. */
  isShippable(item) {
    return item.product_id !== this.productId
      && item.requires_shipping === true
      && item.gift_card !== true;
  }

  /* Counts toward the insured value: everything except the protection item
     and gift cards. Non-shipping add-ons (dye, strap, priority, rush) are
     included on purpose (owner decision 2026-08-26). */
  isInsurable(item) {
    return item.product_id !== this.productId && item.gift_card !== true;
  }

  changeLine(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
  }

  /* Fee = cart value rounded UP to the next $50 tier, times the percent,
     with a minimum. Snapped to the nearest ladder price. */
  desiredVariant(goodsCents) {
    const tierCents = Math.ceil(goodsCents / 5000) * 5000;
    const feeCents = Math.max(this.minimumCents, Math.round(tierCents * this.percent));

    let best = this.variants[0];
    for (const v of this.variants) {
      if (Math.abs(v.priceCents - feeCents) < Math.abs(best.priceCents - feeCents)) {
        best = v;
      }
    }
    return best;
  }

  updateUI(state, desired) {
    this.setAttribute('data-state', state);
    this.priceSpan.textContent = (desired.priceCents / 100).toFixed(2);
    this.toggleBtn.textContent = state === 'on' ? 'Remove' : 'Add';
  }
}

customElements.define('shipping-protection', ShippingProtection);
